/** Accounting · DRE gerencial por competência de faturamento (tela 8.5). */
import { z } from 'zod'
import { dataNominal, uuid } from './comum.js'
import { grupoDaDre } from './plano-contas.js'

export const setorDaDre = z.enum(['Público','Privado'])
export type SetorDaDre = z.infer<typeof setorDaDre>

export const filtrosDaDre = z.object({
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  itemContabilId: uuid.optional(),
  setor: setorDaDre.optional(),
  serie: z.literal('mensal').optional(),
})
export type FiltrosDaDre = z.infer<typeof filtrosDaDre>

/** As 23 linhas da DRE gerencial, na ordem da apresentação (D-169, DS-603). */
export const codigoDaLinhaDaDre = z.enum([
  'receita_propria','receita_repasse','receita_bruta','deducoes','receita_liquida',
  'custos_midia','custos_comissoes','custos_servicos_pj','custos_tecnologia','lucro_bruto',
  'despesas_pessoal','despesas_servicos_pj','despesas_administrativas','despesas_tecnologia',
  'despesas_marketing','despesas_viagens','ebitda',
  'depreciacao_amortizacao','resultado_operacional','resultado_financeiro',
  'resultado_antes_tributos','irpj_csll','resultado_liquido',
])
export type CodigoDaLinhaDaDre = z.infer<typeof codigoDaLinhaDaDre>

export const linhaDaDre = z.object({
  codigo: codigoDaLinhaDaDre,
  rotulo: z.string(),
  tipo: z.enum(['analitica','subtotal','resultado']),
  /**
   * A conta da linha, DERIVADA do plano a cada leitura (DS-603), nunca fixa no código: a conta do
   * grupo quando ele tem uma só, a sintética que agrupa exatamente as contas do grupo, ou nulo quando
   * a linha junta contas de ramos diferentes (a tela então filtra o plano por `grupos`).
   */
  contaCodigo: z.string().nullable(),
  contaId: uuid.nullable(),
  /** Os grupos da DRE que formam a linha; vazio nos subtotais e resultados. */
  grupos: z.array(grupoDaDre),
  realizado: z.string(),
  orcado: z.string().nullable(),
  /**
   * Análise vertical sobre a receita bruta, em FRAÇÃO (`0.8338` = 83,38%).
   *
   * Como todo percentual do fio desde a D-E (05/09/2026) — inclusive
   * `meta.aliquotas[].percentual`, que sempre foi `dv_pct` cru. A borda escala: as telas
   * por `fracaoParaPercentual`, e o PDF do worker pela mesma função.
   */
  avPct: z.string().nullable(),
  naoRateado: z.string().nullable(),
})
export type LinhaDaDre = z.infer<typeof linhaDaDre>

export const pontoDaSerieDaDre = z.object({
  competencia: dataNominal,
  receitaBruta: z.string(),
  margemContribuicao: z.string(),
  ebitda: z.string().nullable(),
  resultadoLiquido: z.string().nullable(),
})
export type PontoDaSerieDaDre = z.infer<typeof pontoDaSerieDaDre>

export const respostaDaDre = z.object({
  data: z.object({
    linhas: z.array(linhaDaDre),
    serie: z.array(pontoDaSerieDaDre),
    kpis: z.object({
      agi: z.string(), agiPct: z.string().nullable(),
      margemContribuicao: z.string(), margemContribuicaoPct: z.string().nullable(),
      ebitda: z.string().nullable(), ebitdaPct: z.string().nullable(),
      resultadoLiquido: z.string().nullable(), resultadoAcumuladoAno: z.string().nullable(),
    }),
    composicao: z.array(z.object({codigo:codigoDaLinhaDaDre,rotulo:z.string(),valor:z.string()})),
  }),
  meta: z.object({
    periodoDe:dataNominal,periodoAte:dataNominal,segmentada:z.boolean(),moeda:z.literal('BRL'),
    entidade:z.object({nome:z.string(),temDados:z.boolean()}),
    aliquotas:z.array(z.object({tributo:z.enum(['ISS','PIS','COFINS','CBS','IBS']),percentual:z.string()})),
    orcamento:z.object({id:uuid,rotulo:z.string()}).nullable(),podeExportar:z.boolean(),
  }),
})
export type RespostaDaDre = z.infer<typeof respostaDaDre>

export const FILA_PDF_DRE = 'dv-pdf-dre'
export const jobDePdfDaDre = z.object({
  entityId:uuid,solicitadoPor:uuid,email:z.email(),nome:z.string().min(1),dre:respostaDaDre,
})
export type JobDePdfDaDre = z.infer<typeof jobDePdfDaDre>
